import React, { useMemo } from 'react';

/**
 * TranscriptViewer - A clean, chat-style viewer for Google Meet transcripts
 *
 * Parses raw transcript content with participant IDs and displays it in a
 * readable format with speaker labels and merged consecutive messages.
 */

// Speaker color palette (using LRBrain theme colors)
const SPEAKER_COLORS = [
  { bg: '#e8f4fc', border: '#1e4a6d', text: '#1e4a6d' },  // Navy
  { bg: '#e6f7f5', border: '#0d9488', text: '#0d7377' },  // Teal
  { bg: '#fef3e6', border: '#d97706', text: '#b45309' },  // Amber
  { bg: '#f3e8ff', border: '#7c3aed', text: '#6d28d9' },  // Purple
  { bg: '#ffe4e6', border: '#e11d48', text: '#be123c' },  // Rose
  { bg: '#dcfce7', border: '#16a34a', text: '#166534' },  // Green
];

const MOJIBAKE_REPLACEMENTS = [
  ['â€™', "'"],
  ['â€˜', "'"],
  ['â€œ', '"'],
  ['â€\u009d', '"'],
  ['â€“', '-'],
  ['â€”', '--'],
  ['â€¦', '...'],
  ['â€¢', '-'],
  ['â†’', '->'],
  ['âœ“', 'OK'],
  ['Â ', ' '],
  ['Â', ''],
];

const sanitizeDisplayText = (value) => {
  if (typeof value !== 'string') return value;
  let text = value.normalize('NFKC');
  for (const [bad, good] of MOJIBAKE_REPLACEMENTS) {
    text = text.split(bad).join(good);
  }
  text = text
    .replace(/\uFFFD/g, '')
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
    .replace(/[\uE000-\uF8FF]/g, '');

  // Remove control chars except tab/newline/carriage-return.
  text = Array.from(text).filter((ch) => {
    const code = ch.charCodeAt(0);
    if (code === 9 || code === 10 || code === 13) return true;
    if (code < 32 || code === 127) return false;
    return true;
  }).join('');
  return text;
};

/**
 * Parse raw transcript content into structured messages
 * @param {string} content - Raw transcript text
 * @returns {Array} - Array of parsed messages with speaker and text
 */
const parseTranscript = (content) => {
  if (!content) return [];

  const normalized = sanitizeDisplayText(content);
  const lines = normalized.split('\n').filter(line => line.trim());
  const messages = [];

  // Regex patterns:
  // 1. Old format: conferenceRecords/{id}/participants/{participantId}: message
  // 2. New format: Display Name: message
  const oldFormatRegex = /^conferenceRecords\/[^/]+\/participants\/(\d+):\s*(.+)$/;
  const newFormatRegex = /^([^:]+):\s*(.+)$/;

  for (const line of lines) {
    // Try old format first
    let match = line.match(oldFormatRegex);
    if (match) {
      const participantId = match[1];
      const text = match[2]?.trim();
      if (text) {
        messages.push({ participantId, text, isId: true });
      }
      continue;
    }

    // Try new format (display name)
    match = line.match(newFormatRegex);
    if (match) {
      const speakerName = match[1]?.trim();
      const text = match[2]?.trim();
      if (speakerName && text) {
        messages.push({ participantId: speakerName, text, isId: false });
      }
    } else if (line.trim() && messages.length > 0) {
      // If line doesn't match any pattern, append to last message
      messages[messages.length - 1].text += ' ' + line.trim();
    }
  }

  return messages;
};

/**
 * Merge consecutive messages from the same speaker
 * @param {Array} messages - Parsed messages
 * @returns {Array} - Merged messages
 */
const mergeConsecutiveMessages = (messages) => {
  if (messages.length === 0) return [];

  const merged = [];
  let current = { ...messages[0] };

  for (let i = 1; i < messages.length; i++) {
    if (messages[i].participantId === current.participantId) {
      // Same speaker - merge the text
      current.text += ' ' + messages[i].text;
    } else {
      // Different speaker - push current and start new
      merged.push(current);
      current = { ...messages[i] };
    }
  }
  merged.push(current);

  return merged;
};

/**
 * Create speaker map with friendly names and colors
 * @param {Array} messages - Parsed messages
 * @returns {Object} - Map of participantId to speaker info
 */
const createSpeakerMap = (messages) => {
  const uniqueParticipants = [...new Set(messages.map(m => m.participantId))];
  const speakerMap = {};

  uniqueParticipants.forEach((participantId, index) => {
    // If it's the old format (numeric ID), use generic Speaker names
    // If it's new format (actual name), use the name directly
    const isNumericId = /^\d+$/.test(participantId);
    const displayName = isNumericId ? `Speaker ${index + 1}` : participantId;

    // Get initials from name (first letter of first and last name, or just first letter)
    let initial;
    if (isNumericId) {
      initial = String.fromCharCode(65 + (index % 26)); // A, B, C, etc.
    } else {
      const nameParts = participantId.split(' ').filter(p => p.length > 0);
      if (nameParts.length >= 2) {
        initial = (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
      } else {
        initial = participantId.substring(0, 2).toUpperCase();
      }
    }

    speakerMap[participantId] = {
      name: displayName,
      color: SPEAKER_COLORS[index % SPEAKER_COLORS.length],
      initial: initial
    };
  });

  return speakerMap;
};

const TranscriptViewer = ({ content }) => {
  const { mergedMessages, speakerMap } = useMemo(() => {
    const parsed = parseTranscript(content);
    const speakers = createSpeakerMap(parsed);
    const merged = mergeConsecutiveMessages(parsed);
    return { mergedMessages: merged, speakerMap: speakers };
  }, [content]);

  if (!content || mergedMessages.length === 0) {
    return (
      <div className="transcript-empty">
        <p>No transcript content available.</p>
      </div>
    );
  }

  return (
    <div className="transcript-viewer">
      {/* Speaker Legend */}
      <div className="transcript-legend">
        {Object.entries(speakerMap).map(([id, speaker]) => (
          <div key={id} className="legend-item">
            <span
              className="legend-avatar"
              style={{
                backgroundColor: speaker.color.bg,
                borderColor: speaker.color.border,
                color: speaker.color.text
              }}
            >
              {speaker.initial}
            </span>
            <span className="legend-name">{speaker.name}</span>
          </div>
        ))}
      </div>

      {/* Messages */}
      <div className="transcript-messages">
        {mergedMessages.map((message, index) => {
          const speaker = speakerMap[message.participantId];
          return (
            <div key={index} className="transcript-message">
              <div
                className="message-avatar"
                style={{
                  backgroundColor: speaker.color.bg,
                  borderColor: speaker.color.border,
                  color: speaker.color.text
                }}
              >
                {speaker.initial}
              </div>
              <div className="message-content">
                <div
                  className="message-speaker"
                  style={{ color: speaker.color.text }}
                >
                  {speaker.name}
                </div>
                <div
                  className="message-bubble"
                  style={{
                    backgroundColor: speaker.color.bg,
                    borderColor: speaker.color.border
                  }}
                >
                  {message.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TranscriptViewer;
